import { RefObject, useEffect, useRef } from 'react'

import type { ElementSaveOptions } from '@/components/ElementDetailV2'

/** Brief delay: portals (react-select) receive focus after cell blur; skip save if focus landed in menu. */
const BLUR_SAVE_MS = 150

function isEventFromTableBodyCell(target: EventTarget | null): boolean {
	if (!target || !(target instanceof Element)) return false
	const td = target.closest('td')
	return Boolean(td && td.closest('tbody'))
}

type UseViewV2TableBlurSaveArgs = {
	containerRef: RefObject<HTMLElement | null>
	dirty: boolean,
	editMode: boolean,
	isSaving: boolean
	onSave?: (opts?: ElementSaveOptions) => void | Promise<void>
}

/**
 * View V2 MRT tables (Columns, Permissions): persist after editing a body cell.
 * Schedules save on each cell blur (including moving to another cell); rapid navigation collapses into one save.
 * Skips when focus ends up in a portaled listbox/menu.
 */
export function useViewV2TableBlurSave({
	containerRef,
	editMode,
	dirty,
	isSaving,
	onSave,
}: UseViewV2TableBlurSaveArgs): void {
	const dirtyRef = useRef(dirty)
	const isSavingRef = useRef(isSaving)
	const onSaveRef = useRef(onSave)
	dirtyRef.current = dirty
	isSavingRef.current = isSaving
	onSaveRef.current = onSave

	useEffect(() => {
		const el = containerRef.current
		if (!el || !editMode || !onSave) return

		let timeoutId: ReturnType<typeof setTimeout> | null = null

		const clearTimer = () => {
			if (timeoutId != null) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
		}

		const runSaveIfAllowed = () => {
			if (!dirtyRef.current || isSavingRef.current) return
			const ae = document.activeElement
			if (
				ae?.closest('[role="listbox"]') ||
				ae?.closest('[role="menu"]')
			) {
				return
			}
			void onSaveRef.current?.({ flushActiveElement: false })
		}

		const scheduleSave = () => {
			clearTimer()
			timeoutId = window.setTimeout(() => {
				timeoutId = null
				runSaveIfAllowed()
			}, BLUR_SAVE_MS)
		}

		const onFocusOutCapture = (event: FocusEvent) => {
			if (!isEventFromTableBodyCell(event.target)) return
			if (!dirtyRef.current || isSavingRef.current) return
			scheduleSave()
		}

		el.addEventListener('focusout', onFocusOutCapture, true)
		return () => {
			clearTimer()
			el.removeEventListener('focusout', onFocusOutCapture, true)
		}
	}, [containerRef, editMode, onSave])
}
